import React, { useRef, useState } from "react";
import { View, Pressable, StyleSheet, StyleProp, ViewStyle, TextStyle } from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { Button } from "@/components/ui/Button";
import useContextMenu from "@/hooks/useContextMenu";
import Animated, { useAnimatedStyle, withTiming, useSharedValue } from "react-native-reanimated";

export type EnumOption = {
  text: string;
  bgColor?: string;
};

export type EnumInputProps = {
  options: (string | EnumOption)[];
  value?: string;
  placeholder?: string;
  label?: string;
  onSelect: (value: string) => void;
  style?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  testID?: string;
  disabled?: boolean;
  badge?: React.ReactNode;
  /** Compact mode for use in headers - matches action button height (36px) */
  compact?: boolean;
};

export function EnumInput({
  options,
  value,
  placeholder = "Select...",
  label,
  onSelect,
  style,
  labelStyle,
  testID,
  disabled = false,
  badge,
  compact = false,
}: EnumInputProps) {
  const { colors } = useTheme();
  const contextMenu = useContextMenu();
  const selectorRef = useRef<View>(null);
  const rotation = useSharedValue(0);
  const [menuVisible, setMenuVisible] = useState(false);

  // Get display text and background color for current value
  const getOptionInfo = (opt: string | EnumOption) => {
    if (typeof opt === "string") {
      return { text: opt, bgColor: undefined };
    }
    return { text: opt.text, bgColor: opt.bgColor };
  };

  const selectedOption = options.find((opt) => getOptionInfo(opt).text === value);
  const selectedInfo = selectedOption ? getOptionInfo(selectedOption) : null;

  const handleToggle = () => {
    if (disabled) return;

    // Toggle based on local state (same pattern as HeaderCell)
    setMenuVisible((isOpen) => {
      contextMenu.setVisible(!isOpen);
      rotation.value = withTiming(isOpen ? 0 : 180, { duration: 150 });
      return !isOpen;
    });

    // Set ignoreRef to prevent click-outside race condition
    contextMenu.setIgnoreRef(selectorRef);

    selectorRef.current?.measureInWindow((x, y, w, h) => {
      contextMenu.setTriggerElement?.({ x, y, width: w ?? 0, height: h ?? 0 });
      contextMenu.setLocation({ x, y: y + (h ?? 0) + 4 });
      contextMenu.setMenuContent(
        <Animated.ScrollView style={{ maxHeight: 300, minWidth: w }}>
          {options.map((opt, index) => {
            const { text, bgColor } = getOptionInfo(opt);
            const isSelected = text === value;
            return (
              <Button
                key={`${text}-${index}`}
                style={[
                  styles.option,
                  {
                    backgroundColor: bgColor || "transparent",
                    borderColor: isSelected ? colors.primary : "transparent",
                    borderWidth: isSelected ? 1 : 0,
                  },
                ]}
                textStyle={[
                  styles.optionText,
                  { color: bgColor ? colors.buttonText : colors.text },
                  isSelected && { fontWeight: "600" },
                ]}
                text={text}
                onPress={() => {
                  onSelect(text);
                  contextMenu.setVisible(false);
                  setMenuVisible(false);
                  rotation.value = withTiming(0, { duration: 150 });
                }}
              />
            );
          })}
        </Animated.ScrollView>,
      );
      contextMenu.setStyle({ backgroundColor: colors.card, overflow: "hidden", borderRadius: 8 });
    });
  };

  const animatedIconStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  return (
    <View style={[styles.container, style]}>
      {label && (
        <View style={styles.labelRow}>
          <ThemedText style={[styles.label, { marginBottom: 0 }, labelStyle]}>{label}</ThemedText>
          {badge}
        </View>
      )}
      <Pressable
        ref={selectorRef}
        testID={testID}
        style={[
          styles.selector,
          compact && styles.selectorCompact,
          {
            backgroundColor: selectedInfo?.bgColor || colors.card,
            borderColor: colors.border,
          },
        ]}
        onPress={handleToggle}
      >
        <ThemedText
          style={[
            styles.selectorText,
            compact && styles.selectorTextCompact,
            {
              color: selectedInfo ? (selectedInfo.bgColor ? colors.buttonText : colors.text) : colors.placeholder,
            },
          ]}
        >
          {selectedInfo?.text || placeholder}
        </ThemedText>
        {!disabled && (
          <Animated.View style={animatedIconStyle}>
            <MaterialIcons
              name="keyboard-arrow-down"
              size={compact ? 20 : 24}
              color={selectedInfo?.bgColor ? colors.buttonText : colors.text}
            />
          </Animated.View>
        )}
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {},
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  label: {
    fontSize: 14,
    marginBottom: 4,
    fontWeight: "600",
  },
  selector: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    height: 40,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  selectorText: {
    fontSize: 16,
    flex: 1,
  },
  selectorCompact: {
    height: 36,
    paddingHorizontal: 10,
  },
  selectorTextCompact: {
    fontSize: 14,
  },
  option: {
    justifyContent: "flex-start",
    marginVertical: 2,
    borderRadius: 6,
  },
  optionText: {
    fontSize: 14,
  },
});
